/**
 * useCodingProjectEditMode - Coding Project AI Development State Hook
 * 
 * Manages AI development state for coding projects,
 * matching the diagram pattern:
 * - Enable/disable AI development mode
 * - Query for existing linked conversation or create new
 * - Link coding project artifact to conversation for AI capability derivation
 * 
 * Coding projects use artifactType: 'coding'
 * Pattern matches useDiagramEditMode.ts for diagrams.
 */

import { useCallback, useState } from 'react';
import { conversationsApi } from '../services/api';
import { logger } from '../utils/logger';

export interface CodingProjectForEdit {
  codingProjectId: string;
  projectId: string;
  title: string;
}

interface UseCodingProjectEditModeDeps {
  // Link-based architecture: auto-create linked conversation on edit
  createConversation: (title?: string) => Promise<string>;
  switchConversation: (id: string, addToVisibleTabs?: boolean) => Promise<void>;
  linkArtifact: (
    artifactType: 'document' | 'ssa' | 'diagram' | 'plan' | 'coding',
    artifactId: string,
    artifactName: string,
    projectId: string,
    conversationId?: string
  ) => Promise<void>;
  updateConversationTitle: (conversationId: string, newTitle: string) => void;
}

interface UseCodingProjectEditModeReturn {
  /** Enable AI development mode for a coding project, auto-linking to AI conversation */
  enableCodingProjectEdit: (codingProject: CodingProjectForEdit) => Promise<void>;
  /** Disable AI development mode (link persists in backend) */
  disableCodingProjectEdit: () => void;
  /** Currently active coding project being developed with AI */
  activeCodingProjectId: string | null;
  /** Loading state while linking */
  isLinking: boolean;
}

export function useCodingProjectEditMode(deps: UseCodingProjectEditModeDeps): UseCodingProjectEditModeReturn {
  const {
    createConversation,
    switchConversation,
    linkArtifact,
    updateConversationTitle,
  } = deps;
  
  // Track which coding project is in AI development mode
  const [activeCodingProjectId, setActiveCodingProjectId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  /**
   * Enable AI development mode for a coding project
   * Queries backend for existing linked conversation, or creates a new one
   * The link persists in the database - robust across hard reloads
   */
  const enableCodingProjectEdit = useCallback(async (codingProject: CodingProjectForEdit) => {
    const { codingProjectId, projectId, title } = codingProject;
    
    logger.debug('[useCodingProjectEditMode] enableCodingProjectEdit called:', { codingProjectId, title });
    
    if (!codingProjectId || !projectId) {
      logger.error('[useCodingProjectEditMode] codingProjectId and projectId are required');
      return;
    }
    
    setIsLinking(true);
    
    try {
      // Set the active coding project
      setActiveCodingProjectId(codingProjectId);
      
      // Query backend for existing conversation linked to this coding project
      // This is robust across hard reloads - link is stored in database
      const result = await conversationsApi.getByArtifact('coding', codingProjectId);
      const existingConversations = result?.conversations || [];
      
      logger.debug('[useCodingProjectEditMode] Backend query result:', { 
        found: existingConversations.length,
        codingProjectId 
      });
      
      // Use friendly title, matching document pattern
      const projectTitle = title || codingProjectId;
      const expectedTitle = `${projectTitle} - Development`;
      
      if (existingConversations.length > 0) {
        // Reuse the most recent linked conversation
        const mostRecent = existingConversations[0];
        logger.debug('[useCodingProjectEditMode] Found existing linked conversation:', mostRecent.conversationId);
        
        // Update title if it doesn't match
        if (mostRecent.title !== expectedTitle) {
          logger.debug('[useCodingProjectEditMode] Updating conversation title:', mostRecent.title, '→', expectedTitle);
          updateConversationTitle(mostRecent.conversationId, expectedTitle);
        }
        
        // Pass true to add to visible tabs (conversation may have been hidden)
        await switchConversation(mostRecent.conversationId, true);
      } else {
        // Create new linked conversation with friendly title
        const newConversationId = await createConversation(expectedTitle);
        
        if (newConversationId) {
          await linkArtifact('coding', codingProjectId, projectTitle, projectId, newConversationId);
          logger.debug('[useCodingProjectEditMode] Created new linked conversation:', newConversationId);
          
          // CRITICAL: Switch to the new conversation to properly load its state from backend
          // This ensures conversation links, tab colors, and link indicators are properly synchronized
          await switchConversation(newConversationId, true);
        }
      }
    } catch (error) {
      logger.error('[useCodingProjectEditMode] Failed to find/create linked conversation:', error);
      // AI development still technically works, just without proper linking
    } finally {
      setIsLinking(false);
    }
  }, [createConversation, switchConversation, linkArtifact, updateConversationTitle]);

  /**
   * Disable AI development mode for coding project
   * Note: The link persists in the backend database
   * This allows reusing the same conversation next time the user enables development
   */
  const disableCodingProjectEdit = useCallback(() => {
    logger.debug('[useCodingProjectEditMode] Disabling coding project AI development mode');
    setActiveCodingProjectId(null);
    logger.debug('[useCodingProjectEditMode] Coding project AI development mode disabled (conversation link persists)');
  }, []);

  return {
    enableCodingProjectEdit,
    disableCodingProjectEdit,
    activeCodingProjectId,
    isLinking,
  };
}
